import Phaser from 'phaser';
import { MetaManager, ACHIEVEMENTS } from '../systems/MetaManager';
import { CampManager } from '../systems/CampManager';
import { ScrollablePanel } from '../ui/ScrollablePanel';
import { GAME_W, GAME_H, GAME_CX, GAME_CY } from '../utils/MobileHelper';
import { ITEM_DEFINITIONS } from '../data/items';
import { startSceneWithFade } from '../utils/SceneUtils';
import { FONT_BODY } from '../utils/Typography';
import { LifetimeStats, formatPlayTime } from '../systems/LifetimeStats';
import { RESOURCE_INFO, ALL_RESOURCES } from '../systems/ResourceManager';
import type { SurvivorClass } from '../systems/CampManager';
import { IslandCampFlag } from '../systems/IslandCampFlag';

// === SCÈNE STATISTIQUES & ACHIEVEMENTS ===

const HEADER_H = 50;

export class StatsScene extends Phaser.Scene {
  private panel!: ScrollablePanel;
  private meta!: MetaManager;
  private camp: CampManager | null = null;

  constructor() {
    super({ key: 'StatsScene' });
  }

  init(data: { meta?: MetaManager; camp?: CampManager }): void {
    this.meta = data?.meta ?? new MetaManager();
    this.camp = data?.camp ?? null;
  }

  create(): void {
    this.cameras.main.fadeIn(200, 0, 0, 0);
    this.add.rectangle(GAME_CX, GAME_CY, GAME_W, GAME_H, 0x0a0a1e).setDepth(0);

    // === HEADER FIXE ===
    this.add.rectangle(GAME_CX, HEADER_H / 2, GAME_W, HEADER_H, 0x0a0a1e).setDepth(10);

    this.add.text(GAME_CX, 15, '📊 STATISTIQUES & SUCCÈS', {
      fontSize: '18px', color: '#f1c40f', fontFamily: FONT_BODY, fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);

    const backBtn = this.add.text(15, 10, '← Retour', {
      fontSize: '11px', color: '#e74c3c', fontFamily: FONT_BODY,
      backgroundColor: '#2c1a1a', padding: { x: 6, y: 3 },
    }).setDepth(11);
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => {
      if (this.camp) startSceneWithFade(this, IslandCampFlag.campSceneKey(), { camp: this.camp, meta: this.meta });
      else startSceneWithFade(this, 'BootScene');
    });
    backBtn.on('pointerover', () => backBtn.setColor('#ff6b6b'));
    backBtn.on('pointerout', () => backBtn.setColor('#e74c3c'));

    const resetBtn = this.add.text(GAME_W - 80, 12, '[Reset]', {
      fontSize: '10px', color: '#7f8c8d', fontFamily: FONT_BODY,
    }).setDepth(11);
    resetBtn.setInteractive({ useHandCursor: true });
    resetBtn.on('pointerdown', () => { this.meta.resetAll(); this.scene.restart({ meta: this.meta, camp: this.camp }); });

    // === CONTENU SCROLLABLE ===
    this.panel = new ScrollablePanel(this, 0, HEADER_H, GAME_W, GAME_H - HEADER_H);
    const stats = this.meta.getStats();

    let y = 15;
    // Sur mobile (450 px), les colonnes 140/420 ne tiennent pas → on stack
    // les achievements en vertical et on ramène les colonnes sur la grille.
    const _mobStats = GAME_W < 500;
    const labelX = _mobStats ? 15 : 140;
    const valueX = _mobStats ? GAME_W - 15 : 420;
    const valueOrigin = _mobStats ? 1 : 0;
    const achLabelX = _mobStats ? 15 : 140;
    const achDescX = _mobStats ? 15 : 360;
    const rewardX = _mobStats ? 25 : 160;

    // Stats globales
    y = this.addSection(y, '--- Statistiques ---');

    const statLines = [
      ['🏴‍☠️ Expéditions', `${stats.totalRuns} (${stats.runsWon}W / ${stats.runsLost}L)`],
      ['⚔️ Ennemis tués', `${stats.enemiesKilled}`],
      ['👹 Boss vaincus', `${stats.bossesKilled}`],
      ['💰 Or gagné', `${stats.totalGoldEarned}`],
      ['💸 Or dépensé', `${stats.totalGoldSpent}`],
      ['🎒 Items lootés', `${stats.itemsLooted}`],
      ['🗺️ Nœuds visités', `${stats.nodesVisited}`],
      ['⛰️ Étage max', `${stats.floorsReached}`],
    ];

    for (const [label, value] of statLines) {
      this.panel.add(this.add.text(labelX, y, label, { fontSize: '12px', color: '#bdc3c7', fontFamily: FONT_BODY }));
      this.panel.add(this.add.text(valueX, y, value, { fontSize: '12px', color: '#3498db', fontFamily: FONT_BODY }).setOrigin(valueOrigin, 0));
      y += 20;
    }

    y += 10;
    y = this.addSection(y, '--- Records ---');

    const records = [
      ['🔪 Meilleure expédition (kills)', `${stats.bestRunKills}`],
      ['💎 Meilleure expédition (or)', `${stats.bestRunGold}`],
    ];
    for (const [label, value] of records) {
      this.panel.add(this.add.text(labelX, y, label, { fontSize: '12px', color: '#bdc3c7', fontFamily: FONT_BODY }));
      this.panel.add(this.add.text(valueX, y, value, { fontSize: '12px', color: '#e67e22', fontFamily: FONT_BODY }).setOrigin(valueOrigin, 0));
      y += 20;
    }

    // === STATS LIFETIME (cumulées sur toutes les sessions) ===
    y += 10;
    y = this.addSection(y, '--- Vie de Capitaine ---');

    const lifetime = LifetimeStats.getAll();
    const totalResources = lifetime.resourcesTotal;
    const mostPlayed = LifetimeStats.getMostPlayedClass();
    const classLabels: Record<SurvivorClass, string> = {
      warrior: '⚔️ Guerrier',
      ranger: '🏹 Archer',
      medic: '💊 Médecin',
    };

    const lifetimeLines: Array<[string, string]> = [
      ['⏱️ Temps de jeu', formatPlayTime(lifetime.totalPlayTimeMs)],
      ['💀 Morts', `${stats.totalDeaths}`],
      ['📦 Ressources récoltées', `${totalResources}`],
      ['🎭 Classe favorite', mostPlayed ? `${classLabels[mostPlayed.cls]} (${mostPlayed.count})` : '—'],
    ];
    for (const [label, value] of lifetimeLines) {
      this.panel.add(this.add.text(labelX, y, label, { fontSize: '12px', color: '#bdc3c7', fontFamily: FONT_BODY }));
      this.panel.add(this.add.text(valueX, y, value, { fontSize: '12px', color: '#9b59b6', fontFamily: FONT_BODY }).setOrigin(valueOrigin, 0));
      y += 20;
    }

    // Détail des ressources si on en a au moins collecté une
    if (totalResources > 0) {
      const detailParts: string[] = [];
      for (const t of ALL_RESOURCES) {
        const n = lifetime.resourcesCollected[t];
        if (n > 0) detailParts.push(`${RESOURCE_INFO[t].emoji}${n}`);
      }
      if (detailParts.length > 0) {
        this.panel.add(this.add.text(labelX, y, detailParts.join('  '), {
          fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
          wordWrap: { width: GAME_W - labelX - 10 },
        }));
        y += _mobStats ? 30 : 22;
      }
    }

    // Détail des classes (uniquement si plusieurs ont été jouées)
    const classesPlayed = Object.entries(lifetime.classesPlayed).filter(([, n]) => n > 0);
    if (classesPlayed.length > 1) {
      const classDetail = classesPlayed
        .map(([cls, n]) => `${classLabels[cls as SurvivorClass]}: ${n}`)
        .join('  ');
      this.panel.add(this.add.text(labelX, y, classDetail, {
        fontSize: '11px', color: '#7f8c8d', fontFamily: FONT_BODY,
        wordWrap: { width: GAME_W - labelX - 10 },
      }));
      y += _mobStats ? 30 : 22;
    }

    // Achievements
    y += 10;
    const unlockCount = this.meta.getUnlockedAchievements().length;
    y = this.addSection(y, `--- Succès (${unlockCount}/${ACHIEVEMENTS.length}) ---`);

    for (const ach of ACHIEVEMENTS) {
      const unlocked = this.meta.isAchievementUnlocked(ach.id);
      const emoji = unlocked ? ach.emoji : '🔒';
      const nameColor = unlocked ? '#2ecc71' : '#7f8c8d';
      const descColor = unlocked ? '#7f8c8d' : '#7f8c8d';

      this.panel.add(this.add.text(achLabelX, y, `${emoji} ${ach.name}`, {
        fontSize: '12px', color: nameColor, fontFamily: FONT_BODY,
        fontStyle: unlocked ? 'bold' : 'normal',
      }));

      // Sur mobile, on stacke la description sous le titre
      const descY = _mobStats ? y + 14 : y + 1;
      this.panel.add(this.add.text(achDescX, descY, ach.description, {
        fontSize: '10px', color: descColor, fontFamily: FONT_BODY,
        wordWrap: { width: GAME_W - achDescX - 10 },
      }));
      const descLineHeight = _mobStats ? 14 + Math.ceil(ach.description.length / 50) * 12 : 0;

      if (ach.unlockItemId) {
        const itemDef = ITEM_DEFINITIONS[ach.unlockItemId];
        const itemName = itemDef ? `${itemDef.emoji} ${itemDef.name}` : '?';
        const rewardY = _mobStats ? y + 14 + descLineHeight : y + 14;
        this.panel.add(this.add.text(rewardX, rewardY, `🎁 Récompense : ${itemName}`, {
          fontSize: '10px', color: unlocked ? '#f39c12' : '#4a4a3a', fontFamily: FONT_BODY,
        }));
        y += _mobStats ? rewardY - y + 16 : 30;
      } else {
        y += _mobStats ? 14 + descLineHeight + 4 : 22;
      }
    }

    y += 40;
    this.panel.setContentHeight(y);
  }

  private addSection(y: number, title: string): number {
    const _mobSec = GAME_W < 500;
    this.panel.add(this.add.text(_mobSec ? 10 : 130, y, title, {
      fontSize: '13px', color: '#f39c12', fontFamily: FONT_BODY,
    }));
    return y + 25;
  }

  shutdown(): void {
    this.panel?.destroy();
  }
}
